import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ShieldCheck, Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import { useStaffAuth } from '../lib/staffAuth';

/**
 * Login del Backoffice interno de Priviax con acceso por código enviado por
 * correo (sin contraseña).
 *
 * Paso 1 (`credentials`): email → backend envía OTP al correo si el email es staff.
 * Paso 2 (`otp`): ingresa el código de 6 dígitos → recibe JWT staff.
 */
export default function PriviaxLogin() {
  const nav = useNavigate();
  const { isAuthed, requestCode, verifyCode, resendCode } = useStaffAuth();
  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState(null); // {challenge_id, expires_at, ttl_seconds}
  const [submitting, setSubmitting] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => { if (isAuthed) nav('/priviax-admin', { replace: true }); }, [isAuthed, nav]);

  // Cuenta regresiva del OTP.
  useEffect(() => {
    if (!challenge) return;
    const tick = () => {
      const secs = Math.max(0, Math.round((new Date(challenge.expires_at) - new Date()) / 1000));
      setRemaining(secs);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [challenge]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const submitCredentials = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await requestCode(email.trim().toLowerCase());
      setChallenge(data);
      setStep('otp');
      setResendCooldown(30);
      if (data.email_sent === false) {
        toast.error('El código se generó pero no pudo enviarse por correo. Revise el detalle abajo.');
      } else {
        toast.success(`Código enviado a ${data.delivery_hint || 'su correo'}. Revise su bandeja (y spam).`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No pudimos enviar el código.');
    } finally { setSubmitting(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return toast.error('Ingrese el código de 6 dígitos.');
    setSubmitting(true);
    try {
      await verifyCode(challenge.challenge_id, code);
      toast.success('Acceso concedido');
      nav('/priviax-admin', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Código incorrecto.');
      setCode('');
    } finally { setSubmitting(false); }
  };

  const resend = async () => {
    if (!challenge || resendCooldown > 0) return;
    setSubmitting(true);
    try {
      const data = await resendCode(challenge.challenge_id);
      setChallenge(data);
      setResendCooldown(30);
      if (data.email_sent === false) {
        toast.error('Se generó un código nuevo pero no pudo enviarse por correo.');
      } else {
        toast.success('Nuevo código enviado.');
      }
    } catch (err) {
      // Si el desafío original expiró/consumió, resetear al paso 1 en vez de bloquear.
      toast.error(err?.response?.data?.detail || 'Sesión de código expirada, empiece de nuevo.');
      setStep('credentials');
      setCode('');
      setChallenge(null);
    } finally { setSubmitting(false); }
  };

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <div className="min-h-screen bg-[#0B1220] flex flex-col items-center justify-center px-4" data-testid="priviax-login">
      <Link to="/" className="text-white/60 hover:text-white text-[12px] mb-6 inline-flex items-center gap-1.5">
        <ArrowLeft size={12} /> Volver al inicio
      </Link>
      <div className="w-full max-w-sm bg-white border border-[#E5E7EB] rounded-md shadow-2xl overflow-hidden">
        <div className="bg-[#143A34] px-5 py-4 flex items-center gap-3 text-white">
          <div className="w-9 h-9 rounded bg-[#2A9B68] flex items-center justify-center">
            <ShieldCheck size={18} strokeWidth={1.8} />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#7EE0AC]">
              Priviax · Backoffice
            </div>
            <div className="text-[15px] font-semibold leading-tight">Acceso restringido</div>
          </div>
        </div>

        {step === 'credentials' && (
          <form onSubmit={submitCredentials} className="p-5 space-y-4" data-testid="staff-step-credentials">
            <div className="text-[12.5px] text-[#6B7280]">
              Ingrese su correo autorizado. Le enviaremos un código de verificación
              de 6 dígitos que expira en 10 minutos.
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Correo</label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                <input
                  type="email" required autoFocus autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border border-[#D1D5DB] rounded-sm text-[13px] focus:outline-none focus:border-[#143A34]"
                  placeholder="jaime@priviax.cl"
                  data-testid="staff-email"
                />
              </div>
            </div>
            <button
              type="submit" disabled={submitting}
              className="w-full py-2 bg-[#143A34] hover:bg-[#0F2A26] text-white text-[13px] font-medium rounded-sm disabled:opacity-50"
              data-testid="staff-submit-credentials"
            >
              {submitting ? 'Enviando código…' : 'Enviar código a mi correo'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={submitOtp} className="p-5 space-y-4" data-testid="staff-step-otp">
            {challenge?.email_sent === false && (
              <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-sm px-3 py-2 text-[12px] text-[#7F1D1D]" data-testid="staff-email-fail">
                <div className="font-semibold text-[#991B1B] mb-0.5">No pudimos entregar el correo</div>
                El servidor de correo rechazó la entrega a <b>{challenge.delivery_hint}</b>. Verifique
                que el buzón exista y sus registros MX. Puede redirigir el código a otro correo
                seteando la variable <code className="font-mono text-[11px]">PRIVIAX_STAFF_OTP_EMAIL</code>.
              </div>
            )}
            <div className="text-[12.5px] text-[#374151]">
              Enviamos un código de 6 dígitos a <b>{challenge?.delivery_hint || email}</b>.
              {remaining > 0 ? (
                <span className="block text-[11.5px] text-[#6B7280] mt-1">
                  Expira en <b className="tabular-nums text-[#143A34]">{mm}:{ss}</b>
                </span>
              ) : (
                <span className="block text-[11.5px] text-[#B91C1C] mt-1">
                  El código expiró. Solicite uno nuevo.
                </span>
              )}
            </div>
            <div>
              <label className="block text-[12px] font-medium text-[#374151] mb-1.5 uppercase tracking-wider">Código</label>
              <input
                type="text" inputMode="numeric" pattern="\d{6}" maxLength={6} required autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-3 border border-[#D1D5DB] rounded-sm text-center text-[22px] tracking-[10px] font-mono focus:outline-none focus:border-[#143A34]"
                placeholder="000000"
                data-testid="staff-otp-code"
              />
            </div>
            <button
              type="submit" disabled={submitting || code.length !== 6 || remaining <= 0}
              className="w-full py-2 bg-[#143A34] hover:bg-[#0F2A26] text-white text-[13px] font-medium rounded-sm disabled:opacity-50"
              data-testid="staff-submit-otp"
            >
              {submitting ? 'Verificando…' : 'Ingresar al Backoffice'}
            </button>
            <div className="flex items-center justify-between text-[11.5px]">
              <button
                type="button" onClick={() => { setStep('credentials'); setCode(''); setChallenge(null); }}
                className="text-[#6B7280] hover:text-[#143A34] inline-flex items-center gap-1"
                data-testid="staff-back-credentials"
              >
                <ArrowLeft size={11} /> Cambiar correo
              </button>
              <button
                type="button" onClick={resend} disabled={resendCooldown > 0 || submitting}
                className="text-[#143A34] hover:underline inline-flex items-center gap-1 disabled:opacity-40 disabled:hover:no-underline"
                data-testid="staff-resend"
              >
                <RefreshCw size={11} /> {resendCooldown > 0 ? `Reenviar (${resendCooldown}s)` : 'Reenviar código'}
              </button>
            </div>
          </form>
        )}
      </div>
      <div className="mt-6 text-[11px] text-white/40 max-w-sm text-center">
        Este acceso es exclusivo del equipo Priviax. Los intentos son auditados y una sesión
        expira automáticamente 2 horas después de verificado el código.
      </div>
    </div>
  );
}
